import { CommonHeader } from "@bcc/components/common-header";
import { AddProductSelectionModal } from "@bcc/components/products/add-product-selection-modal";
import { ManualAddProductModal } from "@bcc/components/products/manual-add-product-modal";
import { ProductsTable } from "@bcc/components/products/products-table";
import { ShopifyConnectModal } from "@bcc/components/products/shopify-connect-modal";
import { ShopifyImportModal } from "@bcc/components/products/shopify-import-modal";
import { Button } from "@bcc/components/ui/button";
import { toast } from "@bcc/hooks/use-toast";
import { checkShopifyConnection } from "@bcc/lib/shopify-service";
import { Loader2, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { setProductsViewTestId } from "./products-view.utils";

interface Variant {
  id: string;
  size: string;
  sku: string;
  status: "published" | "out_of_stock" | "draft" | "rejected";
  subscribers: number;
  purchasePrice: number;
  salePrice: number;
  commission: number;
  payout: number;
  margin: number;
  weeklyLimit: number;
  termType: "long_term" | "short_term";
  termEndDate?: string;
  grindMappings: string[];
  stock: number;
}

export interface Product {
  id: number;
  name: string;
  sku: string;
  category: string;
  imageUrl: string;
  description: string;
  source: "manual" | "shopify";
  variants: Variant[];
}

interface ProductsViewProps {
  productsData: Product[];
  isLoading?: boolean;
  error?: Error;
  testId?: string;
}

export function ProductsView({
  productsData,
  isLoading = false,
  error,
  testId,
}: ProductsViewProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [showManualAddModal, setShowManualAddModal] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [checking, setChecking] = useState(false);
  const prevErrorMsg = useRef<string>();

  // Show error toast when error occurs (prevent duplicate toasts for same error)
  useEffect(() => {
    if (!error) {
      prevErrorMsg.current = undefined;
      return;
    }
    if (error.message !== prevErrorMsg.current) {
      prevErrorMsg.current = error.message;
      toast({
        variant: "destructive",
        title: t("views.products.errors.loadFailed"),
        description: error.message || t("views.products.errors.tryAgainLater"),
      });
    }
  }, [error, t]);

  const handleAddProduct = () => {
    setShowSelectionModal(true);
  };

  const handleSelectManual = () => {
    setShowSelectionModal(false);
    setShowManualAddModal(true);
  };

  const handleSelectShopify = async () => {
    setShowSelectionModal(false);
    setChecking(true);

    try {
      const status = await checkShopifyConnection();

      if (status.connected) {
        setIsConnected(true);
        setShowImportModal(true);
      } else {
        setShowConnectModal(true);
      }
    } catch (error) {
      console.error("[v0] Failed to check Shopify connection:", error);
      setShowConnectModal(true);
    } finally {
      setChecking(false);
    }
  };

  const handleConnected = () => {
    setIsConnected(true);
    setShowImportModal(true);
  };

  const handleProductAdded = () => {
    // In React Router, we'd typically refetch data or use state management
    // For now, this is a placeholder
    window.location.reload();
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        data-testid={testId ?? setProductsViewTestId("container")}
        className="space-y-6"
      >
        <CommonHeader
          title={t("views.products.title")}
          description={t("views.products.description")}
          actions={
            <Button disabled>
              <Plus className="mr-2 h-4 w-4" />
              {t("views.products.addProduct")}
            </Button>
          }
        />

        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {t("views.productsTable.loading")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={testId ?? setProductsViewTestId("container")}
      className="space-y-6"
    >
      <CommonHeader
        title={t("views.products.title")}
        description={t("views.products.description")}
        actions={
          <Button
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={handleAddProduct}
            disabled={checking}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("views.products.addProduct")}
          </Button>
        }
      />

      <ProductsTable data={productsData} />

      <AddProductSelectionModal
        open={showSelectionModal}
        onOpenChange={setShowSelectionModal}
        onSelectManual={handleSelectManual}
        onSelectShopify={handleSelectShopify}
      />

      <ManualAddProductModal
        open={showManualAddModal}
        onOpenChange={setShowManualAddModal}
        onProductAdded={handleProductAdded}
      />

      <ShopifyConnectModal
        open={showConnectModal}
        onOpenChange={setShowConnectModal}
        onConnected={handleConnected}
      />

      <ShopifyImportModal
        open={showImportModal}
        onOpenChange={setShowImportModal}
        onImportComplete={handleProductAdded}
      />
    </div>
  );
}
